import { Check, MapPin, Mic, ScanLine } from 'lucide-react'
import type { Slot } from '../lib/types'
import {
  splitSpeaker,
  canCheckIn, checkInHint, formatClock, relativeLabel, slotState, slotWindow,
  splitTitle,
} from '../lib/slot-time'
import { Button } from '../ui/Button'
import { mytDateKey } from '../config/event'

interface SlotRowProps {
  slot: Slot
  now: number
  index: number
  onOpen: (slot: Slot) => void
  onCheckIn: (slot: Slot) => void
}

export function SlotRow({ slot, now, index, onOpen, onCheckIn }: SlotRowProps) {
  const state = slotState(slot, now)
  const { start, end, unlocksAt } = slotWindow(slot)
  const { label, title } = splitTitle(slot.slot_name)
  const attended = Boolean(slot.checkin_status)
  const open = canCheckIn(slot, now)
  /*
   * "In 2h 22m" (the session) and "Opens in 2h 22m" (its check-in) are the same
   * moment on nearly every slot, and the row was saying it twice. Show the hint only
   * when check-in genuinely opens at a different time from the session, which is
   * what offset_checkin_minute exists to express.
   */
  const rawHint = checkInHint(slot, now)
  const sameMoment = Number.isFinite(unlocksAt) && Math.abs(unlocksAt - start) < 60_000
  /*
   * Countdowns are for today. On another day the tab already says "tomorrow", and
   * "In 22h 6m" beside "Opens in 22h 36m" is two numbers nobody acts on; the time
   * column carries the only fact that matters there.
   */
  const today = Number.isFinite(start) && mytDateKey(start) === mytDateKey(now)
  const showCountdown = today && (state === 'live' || state === 'upcoming')
  const hint = !today || (sameMoment && state === 'upcoming') ? null : rawHint

  return (
    <li
      className="slot rise"
      data-state={state}
      data-attended={attended || undefined}
      style={{ '--i': index } as React.CSSProperties}
    >
      <div className="slot__rail tnum">
        <span className="slot__from">{formatClock(start)}</span>
        <span className="slot__to">{formatClock(end)}</span>
      </div>

      <div className="slot__spine" aria-hidden="true">
        <span className="slot__node">
          {attended ? <Check size={7} strokeWidth={3.5} /> : null}
        </span>
      </div>

      <div className="slot__body">
        {label ? <span className="kicker slot__label">{label}</span> : null}

        {/*
          The whole title is the hit target for the detail sheet — a separate
          "details" affordance would be one more thing to aim at on a phone.
        */}
        <button
          className="slot__title"
          onClick={() => onOpen(slot)}
          style={{ textAlign: 'left', display: 'block' }}
        >
          {title}
        </button>

        <div className="slot__meta">
          {slot.speaker ? (
            <span className="slot__meta-item slot__speaker">
              <Mic size={13} strokeWidth={1.5} />
              <span>
                <strong>{splitSpeaker(slot.speaker).name}</strong>
                {splitSpeaker(slot.speaker).position ? <span className="slot__speaker-position">{splitSpeaker(slot.speaker).position}</span> : null}
              </span>
            </span>
          ) : null}
          <span className="slot__meta-item">
            <MapPin size={13} strokeWidth={1.5} />
            {slot.venue_name}
          </span>
        </div>

        <div className="slot__actions">
          {attended ? (
            <span className="badge badge--done">
              <Check size={11} strokeWidth={2.5} />
              Attended
            </span>
          ) : showCountdown && state === 'live' ? (
            <span className="badge badge--live">
              <span className="badge__dot" />
              {relativeLabel(slot, now)}
            </span>
          ) : showCountdown ? (
            <span className="badge badge--muted">{relativeLabel(slot, now)}</span>
          ) : null}

          {/*
            * Whether a session counts towards the certificate is a property of the
            * session, so it belongs on the session. It used to be expressed only by a
            * "My schedule" tab that filtered the programme down to the scannable ones -
            * which conveyed the distinction by deleting two thirds of the day, taking
            * the meals, the gala dinner and the final day's dispersal off screen with
            * it. Marking adds the information; filtering removed everything else to
            * imply it.
            *
            * The branch this replaced was `!slot.qr_required ? null : null` - both arms
            * null, so nothing was ever shown either way.
            */}
          {!slot.qr_required ? (
            <span className="badge badge--optional">No check-in needed</span>
          ) : attended ? null : state === 'past' && !open ? (
            /*
             * A required session that came and went unrecorded. `!open` matters: for
             * the 20-minute grace after the end, check-in is still open and the button
             * below is showing - "Not recorded" beside "Check in" contradicts itself.
             */
            <span className="badge badge--missed">Not recorded</span>
          ) : open ? null : (
            <span className="badge badge--scan">
              <ScanLine size={11} strokeWidth={2.2} />
              Check-in required
            </span>
          )}

          {open ? (
            <Button variant="primary" size="sm" onClick={() => onCheckIn(slot)}>
              Check in
            </Button>
          ) : hint ? (
            /* Includes "Check-in closed" on a past row today; other days carry no hint. */
            <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
              {hint}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  )
}

/** The dashed rule that marks the present moment between two sessions. */
export function NowLine({ now }: { now: number }) {
  return (
    <li className="nowline" aria-label={`Now ${formatClock(now)}`}>
      <span className="nowline__time tnum">NOW · {formatClock(now)}</span>
      <span className="nowline__dot" aria-hidden="true" />
      <span className="nowline__rule" aria-hidden="true" />
    </li>
  )
}
